import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AnnaWordmark } from '../ui/AnnaWordmark';
import { useSpecSettings } from '../../contexts/SpecSettingsContext';
import { useMotionPreference } from '../../hooks/useMotionPreference';
import { useBootAnimation } from '../../hooks/useBootAnimation';
import { CAPTION_GAP, wordmarkSize } from '../../utils/annaAnchor';
const EASE = [0.23, 1, 0.32, 1] as const;

/**
 * Boot: the word "anna" dissolves into her orb — formed exactly where the real Anna sits on
 * this screen. Then the word returns beneath her with "Klaar voor je instructie", in the same
 * place and style as the page's own idle caption, and the overlay hands over to the living Anna.
 * Tap, click or any key skips.
 */
export function AnnaBoot({
  onDone
}: {
  onDone: () => void;
}) {
  const {
    accentHex
  } = useSpecSettings();
  const reduced = useMotionPreference();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const {
    phase,
    anchor,
    skip
  } = useBootAnimation(canvasRef, accentHex, reduced);
  useEffect(() => {
    if (phase === 'done') onDone();
  }, [phase, onDone]);
  useEffect(() => {
    const onKey = () => skip();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [skip]);
  const size = anchor ? wordmarkSize(anchor.R) : 64;
  return <motion.div role="status" aria-live="polite" aria-label="Anna start op" className="fixed inset-0 z-[100] cursor-pointer bg-night-950" onClick={skip} initial={{
    opacity: 1
  }} exit={{
    opacity: 0,
    transition: {
      duration: 0.3,
      ease: EASE
    }
  }} data-id="element-521" data-mp="39wzt6m97w1wc">
      {!reduced && <canvas ref={canvasRef} aria-hidden className="absolute inset-0" data-id="element-522" data-mp="3ewkwf8vf6wev" />}

      {anchor && <div className="pointer-events-none absolute flex -translate-x-1/2 flex-col items-center text-center" style={{
      left: anchor.cx,
      top: anchor.cy + anchor.R + CAPTION_GAP
    }} data-id="element-523" data-mp="y7fdxx7gua79">
          <AnimatePresence data-id="element-524" data-mp="3n13rby3ma43g">
            {phase === 'ready' && <motion.div key="ready" initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3,
          ease: EASE
        }} className="flex flex-col items-center" data-id="element-525" data-mp="3n2ybha72ds6h">
                <AnnaWordmark size={size} data-id="element-526" data-mp="34ackp4b0krsr" />
                <p className={`mt-3 whitespace-nowrap text-ink-soft ${size >= 64 ? 'text-[24px]' : 'text-[16px]'}`} data-id="element-527" data-mp="34bdul5gan88w">Klaar voor je instructie.</p>
              </motion.div>}
          </AnimatePresence>
        </div>}
      <span className="sr-only" data-id="element-528" data-mp="3d8oc38jpftm9">{phase === 'ready' ? 'Anna is klaar voor je instructie' : 'Anna start op'}</span>
      <p className="pointer-events-none absolute inset-x-0 bottom-10 text-center text-[13px] text-ink-muted" data-id="element-529" data-mp="2ljl53l0fo55x">Tik om over te slaan</p>
    </motion.div>;
}